import React, { useState, useEffect } from "react";
import { Helmet } from 'react-helmet-async';
import { useNavigate, useLocation } from "react-router-dom";
import { X } from 'react-feather';
import Sidebar from './sidebar';
import api from "../api";
import { checkStoreSetupStatus } from "./storeSetupUtils";

function VendorDelivery() {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const navigate = useNavigate();
    const location = useLocation();

    const [setupStatus, setSetupStatus] = useState({
        subscription: false,
        payment: false,
        delivery: false,
        product: false,
    });

    const [errorMsg, setErrorMsg] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [inputFields, setInputFields] = useState([["", ""]]);
    const [existingDeliveries, setExistingDeliveries] = useState([]);
    const [loading, setLoading] = useState(true);

    const token = localStorage.getItem("access_token");

    // ✅ Default suggestions (only used when no deliveries exist)
    const DEFAULT_DELIVERY_SUGGESTIONS = [
        ["Within Nairobi CBD", "200"],
        ["Within Nairobi County", "300"],
        ["Within Kiambu County", "350"],
        ["Within Kajiado County", "350"],
        ["Parcel Services", "300"],
    ];

    useEffect(() => {
        fetchExistingDeliveries();
    }, []);

    const fetchExistingDeliveries = async () => {
        try {
            const response = await api.get(
                baseUrl + '/deliveries/',
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setExistingDeliveries(response.data);

            // ✅ Auto-suggest only once if no deliveries exist
            if (response.data.length === 0 && inputFields.length === 1) {
                setInputFields(DEFAULT_DELIVERY_SUGGESTIONS);
            }

        } catch (error) {
            setErrorMsg('Failed to fetch deliveries');
        } finally {
            setLoading(false);
        }
    };

    const addInputFields = () =>
        setInputFields([...inputFields, ["", ""]]);

    const handleInputChange = (index, subIndex, event) => {
        const values = [...inputFields];
        values[index][subIndex] = event.target.value;
        setInputFields(values);
    };

    const removeInputFields = (index) =>
        setInputFields(inputFields.filter((_, i) => i !== index));

    const deleteDelivery = async (id) => {
        try {
            await api.delete(
                baseUrl + `/deliveries/${id}/`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            fetchExistingDeliveries();
        } catch (error) {
            setErrorMsg('Failed to delete delivery');
        }
    };

    const submitHandler = async () => {
        try {
            const locations = inputFields.map(field => ({
                location: field[0],
                fee: field[1],
            }));

            await api.post(
                baseUrl + '/add-delivery/',
                { locations },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setInputFields([["", ""]]);
            setSuccessMsg('Delivery locations updated successfully!');
            fetchExistingDeliveries();

            await checkStoreSetupStatus({
                navigate,
                location,
                setSetupStatus
            });

        } catch (error) {
            setErrorMsg(error.response?.data?.error || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    setTimeout(() => {
        setErrorMsg('');
        setSuccessMsg('');
    }, 5000);

    const isButtonDisabled =
        inputFields.length === 0 ||
        inputFields.some(pair =>
            pair[0].trim() === '' || pair[1].trim() === ''
        );

    return (
        <>
            <Helmet>
                <title>Deliveries</title>
                <meta name="description" content="Deliveries" />
            </Helmet>

            <section className="container mtopcon dashb">
                <div className="row">
                    <div className="col-12 col-lg-2 dash-min mb-3">
                        <div className="nav-left-sidebar sidebar-dark pc">
                            <div className="menu-list">
                                <nav className="navbar navbar-expand-lg navbar-light nav-l-con">
                                    <div className="collapse navbar-collapse">
                                        <Sidebar />
                                    </div>
                                </nav>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-lg-10 dash-max">
                        <div className="row mb-4 ph-header-con">
                            <h6 className="pageheader-title mb-0">
                                Setup Delivery
                            </h6>
                            <p className="grey-text lh0 font8">
                                Set up delivery options for smooth order fulfillment.
                            </p>
                        </div>

                        <div className="row d-flex justify-content-center mt-4">
                            <div className="col-12 col-lg-4">
                                <div className="card shadow-sm border border-secondary border-opacity-10">
                                    <div className="card-body">

                                        <div className="mb-3">
                                            <label className="form-label font9 mb-0">
                                                Delivery Locations & Fees
                                            </label>
                                            <br />
                                            <label className="form-label font8 grey-text mb-2">
                                                Below is an overview of your delivery locations
                                            </label>

                                            {existingDeliveries.length === 0 && (
                                                <p className="font8 grey-text mb-2">
                                                    We’ve added common delivery options — edit or remove any.
                                                </p>
                                            )}

                                            <div className="row mb-0">
                                                <div className="col-7">
                                                    <label className="form-label font9 mb-0 grey-text">
                                                        Location
                                                    </label>
                                                </div>
                                                <div className="col-4 pl-0">
                                                    <label className="form-label font9 mb-0 grey-text">
                                                        Fee
                                                    </label>
                                                </div>
                                            </div>

                                            {loading && <p>Loading...</p>}

                                            {existingDeliveries.map((delivery) => (
                                                <div key={delivery.id} className="row mb-3">
                                                    <div className="col-7">
                                                        <input
                                                            type="text"
                                                            className="form-control addprobginpt w-100 size_mn"
                                                            value={delivery.location}
                                                            readOnly
                                                        />
                                                    </div>
                                                    <div className="col-4 pl-0">
                                                        <input
                                                            type="number"
                                                            className="form-control addprobginpt w-100 qty_mn"
                                                            value={delivery.fee}
                                                            readOnly
                                                        />
                                                    </div>
                                                    <div className="col-1 p-0 text-center">
                                                        <span onClick={() => deleteDelivery(delivery.id)}>
                                                            <X />
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}

                                            {inputFields.map((pair, index) => (
                                                <div key={index} className="row mb-3">
                                                    <div className="col-7">
                                                        <input
                                                            type="text"
                                                            className="form-control bg-white border w-100 size_mn"
                                                            value={pair[0]}
                                                            onChange={(e) =>
                                                                handleInputChange(index, 0, e)
                                                            }
                                                            placeholder="Onsite, Within Nairobi CBD"
                                                        />
                                                    </div>
                                                    <div className="col-4 pl-0">
                                                        <input
                                                            type="number"
                                                            className="form-control bg-white border w-100 qty_mn"
                                                            value={pair[1]}
                                                            onChange={(e) =>
                                                                handleInputChange(index, 1, e)
                                                            }
                                                            placeholder="200"
                                                        />
                                                    </div>
                                                    <div className="col-1 p-0 text-center">
                                                        <span onClick={() => removeInputFields(index)}>
                                                            <X />
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <div
                                            onClick={addInputFields}
                                            className="font9 pointer"
                                        >
                                            + Add Delivery Location
                                        </div>

                                        <button
                                            type="button"
                                            onClick={submitHandler}
                                            disabled={isButtonDisabled}
                                            className="col-12 btn btn-primary mt-3 big-btn"
                                        >
                                            Update
                                        </button>

                                        {successMsg && (
                                            <p className="text-success text-center mt-3 font8">
                                                <b>{successMsg}</b>
                                            </p>
                                        )}
                                        {errorMsg && (
                                            <p className="text-danger text-center mt-3 font8">
                                                <b>{errorMsg}</b>
                                            </p>
                                        )}

                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>
        </>
    );
}

export default VendorDelivery;
